import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Building2, TrendingUp, Calendar, Bell, AlertTriangle, Users, Radar, X, ArrowUpDown, List, Clock, Newspaper, Eye, Megaphone, ExternalLink, Linkedin, Globe } from 'lucide-react'
import clsx from 'clsx'
import { getDashboardStats, getRadar, runAlertsNow, saveCompany, importFromUrl, getNewCompanies, getDashboardToday } from '../api'
import toast from 'react-hot-toast'
import SalesObjectivesWidget from '../components/SalesObjectivesWidget'
import { classifyProspectQuery } from '../utils/prospectQuery'

const STATUS_LABELS = {
  nouveau: '🆕 Nouveau',
  a_qualifier: '🔍 À qualifier',
  contacte: '📧 Contacté',
  relance: '🔄 Relancé',
  appel: '📞 Appel',
  rdv_planifie: '📅 RDV planifié',
  client_lscf: '🛒 Client LSCF',
  client_gagne: '✅ Client gagné',
  perdu: '❌ Perdu',
}

const STATUS_COLORS = {
  nouveau: '#64748b',
  a_qualifier: '#6366f1',
  contacte: '#3b82f6',
  relance: '#eab308',
  appel: '#f97316',
  rdv_planifie: '#a855f7',
  client_gagne: '#22c55e',
  perdu: '#ef4444',
  client_lscf: '#10b981',
}

const PIE_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#10b981']

function KpiCard({ icon: Icon, label, value, sub, color = 'text-primary-400' }) {
  return (
    <div className="card flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl bg-dark-700/80 flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} className={color} />
      </div>
      <div>
        <p className="text-2xl font-bold text-dark-50">{value}</p>
        <p className="text-dark-400 text-sm">{label}</p>
        {sub && <p className="text-dark-500 text-xs mt-0.5">{sub}</p>}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [radar, setRadar] = useState(null)
  const [today, setToday] = useState(null)
  const [scanning, setScanning] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    getDashboardStats()
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
    getRadar().then(r => setRadar(r.data)).catch(() => {})
    getDashboardToday().then(r => setToday(r.data)).catch(() => {})
  }, [])

  const handleOpenNew = async (p) => {
    try {
      const res = await saveCompany({
        siren: p.siren,
        nom_entreprise: p.nom,
        ville: p.ville || '',
        code_naf: p.code_naf || '',
        departement: p.departement || '',
        date_creation: p.date_creation || '',
        finances_historique: '[]',
        raw_data: '{}',
      })
      if (res.data.already_existed) toast('Déjà en base — ouverture', { icon: 'ℹ️' })
      else toast.success('Ajoutée au CRM')
      navigate('/crm', { state: { openCompanyId: res.data.id } })
    } catch (e) {
      toast.error('Erreur création')
    }
  }

  const [sirenInput, setSirenInput] = useState('')
  const [sirenLoading, setSirenLoading] = useState(false)
  const handleSirenSearch = async () => {
    const classified = classifyProspectQuery(sirenInput)
    if (classified.kind === 'invalid') {
      toast.error("Entre un SIREN (9 chiffres), un SIRET (14 chiffres) ou un lien Pappers/societe.com.")
      return
    }
    setSirenLoading(true)
    try {
      const r = await importFromUrl(classified.value)
      toast.success(r.data.already_existed ? `${r.data.nom} — déjà en base` : `${r.data.nom} importée`)
      navigate('/crm', { state: { openCompanyId: r.data.id } })
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Entreprise introuvable')
    } finally {
      setSirenLoading(false)
    }
  }

  const [newCompModal, setNewCompModal] = useState(false)
  const [newCompList, setNewCompList] = useState([])
  const [newCompSort, setNewCompSort] = useState('recent')
  const [newCompLoading, setNewCompLoading] = useState(false)
  const loadNewCompanies = async (sort) => {
    setNewCompLoading(true)
    try {
      const r = await getNewCompanies(6, sort)
      setNewCompList(r.data.companies || [])
    } catch (e) {
      toast.error('Erreur chargement')
    } finally {
      setNewCompLoading(false)
    }
  }
  const openNewCompModal = () => {
    setNewCompModal(true)
    loadNewCompanies(newCompSort)
  }
  const toggleNewCompSort = () => {
    const s = newCompSort === 'recent' ? 'old' : 'recent'
    setNewCompSort(s)
    loadNewCompanies(s)
  }

  const handleScan = async () => {
    setScanning(true)
    try {
      const r = await runAlertsNow()
      toast.success(`Scan: ${r.data.new_prospects || 0} nouveaux · ${r.data.signals || 0} signaux`)
      const rad = await getRadar()
      setRadar(rad.data)
    } catch (e) {
      toast.error('Erreur scan')
    } finally {
      setScanning(false)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
    </div>
  )

  if (!stats) return null

  const pipelineData = Object.entries(stats.pipeline).map(([key, count]) => ({
    name: STATUS_LABELS[key] || key,
    count,
    color: STATUS_COLORS[key],
  }))

  const creditPct = Math.min(100, (stats.pappers_credits_used / stats.pappers_credits_limit) * 100)

  const EVENT_EMOJI = { rappel: '🔔', appel: '📞', rdv: '📅', note: '📝' }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ───────── OBJECTIFS COMMERCIAUX — cadences Nicolas ───────── */}
      <SalesObjectivesWidget />

      {/* ───────── CENTRE DE PILOTAGE — Priorités du jour ───────── */}
      {today && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Aujourd'hui : agenda + relances */}
          <div className="lg:col-span-5 card border-primary-700/40">
            <h3 className="text-dark-50 font-semibold flex items-center gap-2 mb-3">
              <Clock size={18} className="text-primary-400" /> Aujourd'hui
            </h3>
            {/* RDV / rappels du jour */}
            <p className="text-dark-400 text-xs uppercase font-medium mb-1.5">📆 Agenda du jour</p>
            {today.agenda_today.length === 0 ? (
              <p className="text-dark-500 text-xs mb-3">Rien de prévu aujourd'hui.</p>
            ) : (
              <div className="space-y-1 mb-3">
                {today.agenda_today.map(e => (
                  <div key={e.id} onClick={() => navigate('/agenda')}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-dark-700/40 cursor-pointer text-xs">
                    <span className="text-dark-400 w-10 tabular-nums">{e.all_day ? '—' : (e.start_dt || '').slice(11, 16)}</span>
                    <span>{EVENT_EMOJI[e.event_type] || '•'}</span>
                    <span className="text-dark-50 truncate flex-1">{e.title}</span>
                    {e.location && <span className="text-dark-500 truncate max-w-[90px]">{e.location}</span>}
                  </div>
                ))}
              </div>
            )}
            {/* Relances en retard */}
            <p className="text-dark-400 text-xs uppercase font-medium mb-1.5">🔄 Relances à faire</p>
            {today.relances.length === 0 ? (
              <p className="text-dark-500 text-xs">Aucune relance en attente. 👌</p>
            ) : (
              <div className="space-y-1">
                {today.relances.slice(0, 6).map((r, i) => (
                  <div key={i} onClick={() => navigate('/crm', { state: { openCompanyId: r.company_id } })}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-dark-700/40 cursor-pointer text-xs">
                    <span className="text-amber-400">●</span>
                    <span className="text-dark-50 truncate flex-1">{r.company_nom}</span>
                    <span className="text-dark-500 truncate max-w-[120px]">{r.action || r.date}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Planning marketing de la semaine */}
          <div className="lg:col-span-4 card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-dark-50 font-semibold flex items-center gap-2">
                <Megaphone size={18} className="text-pink-400" /> Marketing
              </h3>
              <button onClick={() => navigate('/linkedin')} className="text-xs text-primary-400 hover:text-primary-300">Planifier →</button>
            </div>
            {/* Objectif 3 publications / semaine */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-dark-400">Publications cette semaine</span>
                <span className="text-dark-200 font-medium">{today.marketing_published_week} / {today.marketing_goal_week}</span>
              </div>
              <div className="h-2 rounded-full bg-dark-700 overflow-hidden">
                <div className="h-full bg-pink-500 transition-all"
                  style={{ width: `${Math.min(100, (today.marketing_published_week / today.marketing_goal_week) * 100)}%` }} />
              </div>
            </div>
            <p className="text-dark-400 text-xs uppercase font-medium mb-1.5">📅 Programmé cette semaine</p>
            {today.marketing_week.length === 0 ? (
              <p className="text-dark-500 text-xs">Rien de programmé. Crée tes posts (site web + LinkedIn) et planifie-les.</p>
            ) : (
              <div className="space-y-1">
                {today.marketing_week.map(p => (
                  <div key={p.id} onClick={() => navigate('/linkedin')}
                    className="flex items-center gap-2 p-1.5 rounded hover:bg-dark-700/40 cursor-pointer text-xs">
                    {p.channel === 'site_web' ? <Globe size={13} className="text-sky-400" /> : <Linkedin size={13} className="text-blue-400" />}
                    <span className="text-dark-50 truncate flex-1">{p.title}</span>
                    <span className={clsx('px-1.5 py-0.5 rounded text-[10px]',
                      p.status === 'publié' ? 'bg-green-600/20 text-green-300' :
                      p.status === 'prêt' ? 'bg-blue-600/20 text-blue-300' : 'bg-dark-700 text-dark-400')}>
                      {(p.scheduled_date || '').slice(5, 10)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Veille : nouveautés fournisseurs + LinkedIn à revérifier */}
          <div className="lg:col-span-3 card">
            <h3 className="text-dark-50 font-semibold flex items-center gap-2 mb-3">
              <Newspaper size={18} className="text-amber-400" /> Veille
            </h3>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-dark-400 text-xs uppercase font-medium">🆕 Nouveautés fournisseurs</p>
              {today.news_unseen_count > 0 && <span className="text-[10px] px-1.5 rounded-full bg-amber-500/20 text-amber-300">{today.news_unseen_count}</span>}
            </div>
            {today.supplier_news.length === 0 ? (
              <p className="text-dark-500 text-xs mb-3">Rien de neuf.</p>
            ) : (
              <div className="space-y-1.5 mb-3">
                {today.supplier_news.slice(0, 4).map(n => (
                  <a key={n.id} href={n.link} target="_blank" rel="noreferrer"
                    className="block text-xs hover:text-primary-400">
                    <span className="text-dark-100 line-clamp-1">{n.title}</span>
                    <span className="text-dark-500 text-[10px]">{n.supplier_name}</span>
                  </a>
                ))}
              </div>
            )}
            <p className="text-dark-400 text-xs uppercase font-medium mb-1.5 flex items-center gap-1"><Eye size={12} /> LinkedIn à revérifier</p>
            {today.due_watches.length === 0 ? (
              <p className="text-dark-500 text-xs">Tout est à jour.</p>
            ) : (
              <div className="space-y-1">
                {today.due_watches.slice(0, 4).map(w => (
                  <a key={w.id} href={w.linkedin_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-xs text-dark-100 hover:text-blue-400">
                    <Linkedin size={12} className="text-blue-400" />
                    <span className="truncate flex-1">{w.label}</span>
                    <ExternalLink size={10} className="text-dark-500" />
                  </a>
                ))}
                {today.due_watches.length > 4 && (
                  <button onClick={() => navigate('/veille-linkedin')} className="text-[11px] text-primary-400 hover:text-primary-300">+ {today.due_watches.length - 4} autre(s)</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recherche par SIREN / SIRET */}
      <div className="card flex items-center gap-3 flex-wrap">
        <span className="text-dark-50 font-semibold text-sm whitespace-nowrap">🔎 Recherche SIREN / SIRET</span>
        <input
          className="input-field text-sm flex-1 min-w-[200px]"
          placeholder="SIREN, SIRET ou URL (ex: 917659237 ou lien Pappers/societe.com)"
          value={sirenInput}
          onChange={e => setSirenInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSirenSearch()}
        />
        <button onClick={handleSirenSearch} disabled={sirenLoading}
          className="btn-primary text-sm">
          {sirenLoading ? 'Recherche…' : 'Trouver + importer'}
        </button>
      </div>

      {/* RADAR — Phase 3 */}
      {radar && (
        <div className="card border-primary-700/40">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-dark-50 font-semibold flex items-center gap-2">
              <Radar size={18} className="text-primary-400" /> Radar prospection du jour
            </h3>
            <button onClick={handleScan} disabled={scanning} className="btn-secondary text-xs">
              {scanning ? 'Scan en cours…' : '🔄 Scanner maintenant'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Top prospects */}
            <div>
              <p className="text-dark-400 text-xs uppercase font-medium mb-2">🔥 Top prospects à contacter</p>
              {radar.top_prospects.length === 0 ? (
                <p className="text-dark-500 text-xs">Aucun prospect scoré. Lance le scoring IA.</p>
              ) : (
                <div className="space-y-1">
                  {radar.top_prospects.slice(0, 8).map(p => (
                    <div key={p.id}
                      onClick={() => navigate('/crm', { state: { openCompanyId: p.id } })}
                      className="flex items-center gap-2 p-1.5 rounded hover:bg-dark-700/40 cursor-pointer text-xs">
                      <span className={clsx(
                        'font-bold w-7 text-center rounded',
                        p.score >= 80 ? 'text-green-400' : p.score >= 60 ? 'text-blue-400' : 'text-yellow-400'
                      )}>{p.score}</span>
                      <span className="text-dark-50 truncate flex-1">{p.nom}</span>
                      <span className="text-dark-500">{p.ville}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Jeunes entreprises cibles */}
            <div>
              <p className="text-dark-400 text-xs uppercase font-medium mb-2">🌱 Jeunes entreprises cibles (&lt; 2 ans)</p>
              {(!radar.young_targets || radar.young_targets.length === 0) ? (
                <p className="text-dark-500 text-xs">Aucune. Lance un scan.</p>
              ) : (
                <div className="space-y-1">
                  {radar.young_targets.slice(0, 8).map((c) => (
                    <div key={c.id}
                      onClick={() => navigate('/crm', { state: { openCompanyId: c.id } })}
                      className="p-1.5 rounded bg-dark-900/40 hover:bg-dark-700/40 cursor-pointer text-xs">
                      <p className="text-dark-50 truncate">{c.nom}</p>
                      <p className="text-dark-500">{c.ville} · créée {(c.date_creation || '').slice(0, 7)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Nouveaux prospects détectés */}
            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-dark-400 text-xs uppercase font-medium">🆕 Nouvelles entreprises détectées</p>
                <button onClick={openNewCompModal}
                  className="btn-secondary text-[10px] py-1 px-2 flex items-center gap-1 whitespace-nowrap">
                  <List size={11} /> Voir tout (&lt; 6 mois)
                </button>
              </div>
              {radar.new_prospects.length === 0 ? (
                <p className="text-dark-500 text-xs">Crée une alerte pour détecter les nouvelles boîtes.</p>
              ) : (
                <div className="space-y-1">
                  {radar.new_prospects.slice(0, 8).map((p, i) => (
                    <div key={i}
                      onClick={() => handleOpenNew(p)}
                      className="p-1.5 rounded bg-dark-900/40 hover:bg-primary-900/30 cursor-pointer text-xs flex items-center gap-2 group">
                      <div className="flex-1 min-w-0">
                        <p className="text-dark-50 truncate">{p.nom}</p>
                        <p className="text-dark-500">{p.ville} · {p.code_naf}{p.date_creation ? ' · ' + p.date_creation.slice(0, 7) : ''}</p>
                      </div>
                      <span className="text-primary-400 opacity-0 group-hover:opacity-100 flex-shrink-0">+ Ajouter</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KPIs row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Building2} label="Entreprises en base" value={stats.total_companies.toLocaleString('fr-FR')} color="text-primary-400" />
        <KpiCard icon={TrendingUp} label="Client gagnés" value={stats.pipeline.client_gagne || 0} color="text-green-400" />
        <KpiCard icon={Calendar} label="RDV planifiés" value={stats.pipeline.rdv_planifie || 0} color="text-purple-400" />
        <KpiCard
          icon={Bell}
          label="Actions aujourd'hui"
          value={stats.upcoming_actions.today.length}
          sub={`${stats.upcoming_actions.week.length} cette semaine`}
          color="text-yellow-400"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* NAF pie */}
        <div className="card">
          <h3 className="text-dark-50 font-semibold mb-4">Répartition par code NAF</h3>
          {stats.by_naf.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={stats.by_naf}
                  dataKey="count"
                  nameKey="code"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ code, percent }) => `${code} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {stats.by_naf.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  formatter={(val, name, props) => [val, props.payload.label || name]}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-dark-500">
              Aucune donnée — lancez une recherche
            </div>
          )}
        </div>

        {/* Department bar */}
        <div className="card">
          <h3 className="text-dark-50 font-semibold mb-4">Top 10 départements</h3>
          {stats.by_departement.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.by_departement.slice(0, 10)} margin={{ left: -20 }}>
                <XAxis dataKey="departement" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-dark-500">
              Aucune donnée
            </div>
          )}
        </div>
      </div>

      {/* Pipeline row */}
      <div className="card">
        <h3 className="text-dark-50 font-semibold mb-4">Pipeline de prospection</h3>
        <div className="grid grid-cols-8 gap-2">
          {Object.entries(stats.pipeline).map(([key, count]) => (
            <div
              key={key}
              className="bg-dark-900/50 border border-dark-700 rounded-xl p-3 text-center cursor-pointer hover:border-primary-500/50 transition-colors"
              onClick={() => navigate('/crm')}
            >
              <p className="text-2xl font-bold text-dark-50">{count}</p>
              <p className="text-dark-400 text-xs mt-1 leading-tight">{STATUS_LABELS[key]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent companies */}
        <div className="card">
          <h3 className="text-dark-50 font-semibold mb-4">Entreprises récentes en base</h3>
          <div className="space-y-3">
            {[
              { label: 'Créées < 6 mois', count: stats.recent_companies['6_months'], color: 'text-green-400' },
              { label: 'Créées < 1 an', count: stats.recent_companies['1_year'], color: 'text-primary-400' },
              { label: 'Créées < 2 ans', count: stats.recent_companies['2_years'], color: 'text-purple-400' },
            ].map(({ label, count, color }) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-dark-400 text-sm">{label}</span>
                <span className={`font-bold text-lg ${color}`}>{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming actions */}
        <div className="card">
          <h3 className="text-dark-50 font-semibold mb-4">Prochaines actions</h3>
          {stats.upcoming_actions.week.length === 0 ? (
            <p className="text-dark-500 text-sm">Aucune action planifiée cette semaine</p>
          ) : (
            <div className="space-y-2">
              {stats.upcoming_actions.week.slice(0, 5).map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-dark-900/50 rounded-lg p-2.5">
                  <div>
                    <p className="text-dark-200 text-sm">{a.action}</p>
                    <p className="text-dark-500 text-xs">Entreprise #{a.company_id}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${a.date === new Date().toISOString().split('T')[0] ? 'bg-red-900/50 text-red-300' : 'bg-dark-700 text-dark-300'}`}>
                    {a.date}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Credits */}
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-dark-50 font-semibold">Crédits Pappers API</h3>
          <span className={`text-sm font-medium ${creditPct > 80 ? 'text-red-400' : 'text-green-400'}`}>
            {stats.pappers_credits_used.toLocaleString('fr-FR')} / {stats.pappers_credits_limit.toLocaleString('fr-FR')}
          </span>
        </div>
        <div className="w-full h-2 bg-dark-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${creditPct > 80 ? 'bg-red-500' : creditPct > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
            style={{ width: `${creditPct}%` }}
          />
        </div>
        {creditPct > 80 && (
          <div className="flex items-center gap-2 mt-2 text-yellow-400 text-sm">
            <AlertTriangle size={14} />
            <span>Quota presque atteint — pensez à augmenter votre limite</span>
          </div>
        )}
      </div>

      {/* Modal nouvelles entreprises < 6 mois */}
      {newCompModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setNewCompModal(false)}>
          <div className="card max-w-3xl w-full max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-dark-50 font-semibold flex items-center gap-2">
                🆕 Nouvelles entreprises (&lt; 6 mois)
                <span className="text-dark-500 text-sm font-normal">{newCompList.length}</span>
              </h3>
              <div className="flex items-center gap-2">
                <button onClick={toggleNewCompSort}
                  className="btn-secondary text-xs flex items-center gap-1">
                  <ArrowUpDown size={12} />
                  {newCompSort === 'recent' ? 'Plus récent → ancien' : 'Plus ancien → récent'}
                </button>
                <button onClick={() => setNewCompModal(false)}
                  className="text-dark-400 hover:text-dark-50">
                  <X size={20} />
                </button>
              </div>
            </div>

            {newCompLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin w-7 h-7 border-2 border-primary-500 border-t-transparent rounded-full" />
              </div>
            ) : newCompList.length === 0 ? (
              <p className="text-dark-500 text-sm py-8 text-center">
                Aucune entreprise créée il y a moins de 6 mois en base.
              </p>
            ) : (
              <div className="overflow-y-auto -mx-2 px-2">
                <table className="w-full text-xs">
                  <thead className="text-dark-400 uppercase text-[10px] sticky top-0 bg-dark-800">
                    <tr className="border-b border-dark-700">
                      <th className="text-left py-2 px-2">Entreprise</th>
                      <th className="text-left py-2 px-2">Ville</th>
                      <th className="text-left py-2 px-2">Code NAF</th>
                      <th className="text-left py-2 px-2">Activité</th>
                      <th className="text-left py-2 px-2">Créée le</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newCompList.map(c => (
                      <tr key={c.id}
                        onClick={() => navigate('/crm', { state: { openCompanyId: c.id } })}
                        className="border-b border-dark-700/50 hover:bg-dark-700/40 cursor-pointer">
                        <td className="py-2 px-2 text-dark-50 font-medium">{c.nom}</td>
                        <td className="py-2 px-2 text-dark-400">{c.ville || '—'}</td>
                        <td className="py-2 px-2 text-dark-300 whitespace-nowrap">{c.code_naf || '—'}</td>
                        <td className="py-2 px-2 text-dark-400">
                          {c.real_category || c.libelle_naf || '—'}
                        </td>
                        <td className="py-2 px-2 text-dark-300 whitespace-nowrap">
                          {c.date_creation || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
